/**
 * Sector operations — Places and levels data model.
 * R39 A2.a (plan docs/plans/sector-operations.md §4).
 */

import {
  OPS_SECTOR_KEYS,
  type OpsSector,
  SITES_PRESETS,
  type SitesPreset,
  SHOPS_PRESETS,
  type ShopsPreset,
  STAYS_PRESETS,
  type StaysPreset,
  CLINICS_SITE_KINDS,
  type ClinicsSiteKind,
  CLINICS_PRESET_FAMILIES,
  type ClinicsPresetFamily,
  CLINICS_PRESETS,
  type ClinicsPreset,
  CLINICS_PRESET_METADATA,
  type ClinicsPresetMetadata,
  type OpsPreset,
  SHOPS_ZONE_KINDS,
  type ShopsZoneKind,
  STAYS_BLOCK_KINDS,
  type StaysBlockKind,
  STAYS_AREA_KINDS,
  type StaysAreaKind,
  STAYS_PLACE_WRITE_KEYS,
  type StaysPlaceWriteKey,
  JOBS_PLACE_WRITE_KEYS,
  type JobsPlaceWriteKey,
  CLINICS_PLACE_WRITE_KEYS,
  type ClinicsPlaceWriteKey,
  PLANT_PRESETS,
  type PlantPreset,
  type OpsPlaceLevelPlant,
  PLANT_PLACE_LEVELS,
  type OpsPlaceLevelConstruction,
  type OpsPlaceLevelHospital,
  type OpsPlaceLevelSites,
  type OpsPlaceLevelShops,
  type OpsPlaceLevelStays,
  type OpsPlaceLevelJobs,
  type OpsPlaceLevelClinics,
  type OpsPlaceLevel,
  CONSTRUCTION_PLACE_LEVELS,
  HOSPITAL_PLACE_LEVELS,
  SITES_PLACE_LEVELS,
  SHOPS_PLACE_LEVELS,
  STAYS_PLACE_LEVELS,
  JOBS_PLACE_LEVELS,
  CLINICS_PLACE_LEVELS,
  type OpsSectorDefinition,
  OPS_SECTORS,
  isOpsSector,
  sectorRootLevels,
  isValidChildLevel,
  defaultChildLevel,
  sectorAllowsPresetOnLevel,
  findOpsSectorByRouteSegment,
  findOpsSectorByView,
  isHospitalSector,
  isClinicsSector,
  isPlantSector,
  isMultiResourceBookingSector,
  isSampleCustodySector,
  isAnonymousBookingReminderSector,
} from './sectors.js'

export {
  OPS_SECTOR_KEYS,
  type OpsSector,
  SITES_PRESETS,
  type SitesPreset,
  SHOPS_PRESETS,
  type ShopsPreset,
  STAYS_PRESETS,
  type StaysPreset,
  CLINICS_SITE_KINDS,
  type ClinicsSiteKind,
  CLINICS_PRESET_FAMILIES,
  type ClinicsPresetFamily,
  CLINICS_PRESETS,
  type ClinicsPreset,
  CLINICS_PRESET_METADATA,
  type ClinicsPresetMetadata,
  type OpsPreset,
  SHOPS_ZONE_KINDS,
  type ShopsZoneKind,
  STAYS_BLOCK_KINDS,
  type StaysBlockKind,
  STAYS_AREA_KINDS,
  type StaysAreaKind,
  STAYS_PLACE_WRITE_KEYS,
  type StaysPlaceWriteKey,
  JOBS_PLACE_WRITE_KEYS,
  type JobsPlaceWriteKey,
  CLINICS_PLACE_WRITE_KEYS,
  type ClinicsPlaceWriteKey,
  PLANT_PRESETS,
  type PlantPreset,
  type OpsPlaceLevelPlant,
  PLANT_PLACE_LEVELS,
  type OpsPlaceLevelConstruction,
  type OpsPlaceLevelHospital,
  type OpsPlaceLevelSites,
  type OpsPlaceLevelShops,
  type OpsPlaceLevelStays,
  type OpsPlaceLevelJobs,
  type OpsPlaceLevelClinics,
  type OpsPlaceLevel,
  CONSTRUCTION_PLACE_LEVELS,
  HOSPITAL_PLACE_LEVELS,
  SITES_PLACE_LEVELS,
  SHOPS_PLACE_LEVELS,
  STAYS_PLACE_LEVELS,
  JOBS_PLACE_LEVELS,
  CLINICS_PLACE_LEVELS,
  type OpsSectorDefinition,
  OPS_SECTORS,
  isOpsSector,
  sectorRootLevels,
  isValidChildLevel,
  defaultChildLevel,
  sectorAllowsPresetOnLevel,
  findOpsSectorByRouteSegment,
  findOpsSectorByView,
  isHospitalSector,
  isClinicsSector,
  isPlantSector,
  isMultiResourceBookingSector,
  isSampleCustodySector,
  isAnonymousBookingReminderSector,
}

export const DEFAULT_CHILD_LEVEL: Record<OpsSector, Record<string, OpsPlaceLevel>> = {
  construction: OPS_SECTORS.construction.defaultChild as Record<string, OpsPlaceLevel>,
  hospital: OPS_SECTORS.hospital.defaultChild as Record<string, OpsPlaceLevel>,
  sites: OPS_SECTORS.sites.defaultChild as Record<string, OpsPlaceLevel>,
  delivery: {},
  shops: OPS_SECTORS.shops.defaultChild as Record<string, OpsPlaceLevel>,
  stays: OPS_SECTORS.stays.defaultChild as Record<string, OpsPlaceLevel>,
  jobs: OPS_SECTORS.jobs.defaultChild as Record<string, OpsPlaceLevel>,
  clinics: OPS_SECTORS.clinics.defaultChild as Record<string, OpsPlaceLevel>,
  plant: OPS_SECTORS.plant.defaultChild as Record<string, OpsPlaceLevel>,
}

export const ROOT_LEVEL: Partial<Record<OpsSector, OpsPlaceLevel>> = {
  construction: 'SITE',
  hospital: 'FACILITY',
  sites: 'CLIENT',
  shops: 'GROUP',
  stays: 'GROUP',
  clinics: 'ORG',
  plant: 'PLANT',
}

/** Allowed child level transitions per sector. */
export const ALLOWED_CHILD_LEVELS: Record<OpsSector, Record<string, readonly OpsPlaceLevel[]>> = {
  construction: OPS_SECTORS.construction.allowedChildren as Record<string, readonly OpsPlaceLevel[]>,
  hospital: OPS_SECTORS.hospital.allowedChildren as Record<string, readonly OpsPlaceLevel[]>,
  sites: OPS_SECTORS.sites.allowedChildren as Record<string, readonly OpsPlaceLevel[]>,
  delivery: {},
  shops: OPS_SECTORS.shops.allowedChildren as Record<string, readonly OpsPlaceLevel[]>,
  stays: OPS_SECTORS.stays.allowedChildren as Record<string, readonly OpsPlaceLevel[]>,
  jobs: OPS_SECTORS.jobs.allowedChildren as Record<string, readonly OpsPlaceLevel[]>,
  clinics: OPS_SECTORS.clinics.allowedChildren as Record<string, readonly OpsPlaceLevel[]>,
  plant: OPS_SECTORS.plant.allowedChildren as Record<string, readonly OpsPlaceLevel[]>,
}

/** Checks whether a target level sits strictly below a root level in a given sector hierarchy. */
export function isLevelBelowRoot(
  sector: OpsSector,
  rootLevel: OpsPlaceLevel,
  targetLevel: OpsPlaceLevel,
): boolean {
  if (rootLevel === targetLevel) return false
  const allowed = ALLOWED_CHILD_LEVELS[sector]
  if (!allowed) return false
  const visited = new Set<string>()
  const queue: string[] = [rootLevel]
  while (queue.length > 0) {
    const current = queue.shift()!
    const children = allowed[current] || []
    for (const child of children) {
      if (child === targetLevel) return true
      if (!visited.has(child)) {
        visited.add(child)
        queue.push(child)
      }
    }
  }
  return false
}

export interface StaysRoomType {
  id: string
  name: string
  requiredPhotos: string[]
  standardCleanMinutes: number
}

export const MAX_PLACE_ACCESS_UIDS = 200

export type OpsAccessJobState = 'QUEUED' | 'RUNNING' | 'DONE' | 'FAILED' | 'SUPERSEDED'

export interface OpsPlaceAccessJobProgress {
  jobId: string
  state: OpsAccessJobState
  updated: number
  startedAt: number
}

export interface OpsPlaceDutyChain {
  inChargeRoleKey: string | null
  backupRoleKey: string | null
  onCallRoleKey: string | null
}

export interface JobSiteAddress {
  line1: string
  line2?: string | null
  locality?: string | null
  city: string
  region?: string | null
  postcode?: string | null
}

export interface JobSiteGeo {
  lat: number
  lng: number
}

export interface CreateJobSiteInput {
  workspaceId: string
  customerId: string
  parentAreaId?: string | null
  name: string
  code?: string
  address: JobSiteAddress
  geo?: JobSiteGeo | null
  accessNotes?: string | null
  opId?: string
}

export interface UpdateJobSiteInput {
  workspaceId: string
  placeId: string
  name?: string
  address?: JobSiteAddress
  geo?: JobSiteGeo | null
  accessNotes?: string | null
  opId?: string
}

export interface CreateJobSiteResult {
  placeId: string
  path: string[]
  replayed?: boolean
}

export interface UpdateJobSiteResult {
  success: boolean
  placeId: string
  replayed?: boolean
}

export interface OpsPlace {
  id?: string
  workspaceId: string
  sector: OpsSector
  parentId: string | null
  path: string[]
  level: OpsPlaceLevel
  name: string
  code: string
  preset?: OpsPreset | null
  siteKind?: ClinicsSiteKind | null
  zoneKind?: ShopsZoneKind | null
  blockKind?: StaysBlockKind | null
  areaKind?: StaysAreaKind | null
  roomTypes?: StaysRoomType[] | null
  roomTypeId?: string | null
  minibarCheckOnCheckout?: boolean | null
  guestRequestsEnabled?: boolean | null
  customerId?: string | null
  address?: JobSiteAddress | null
  geo?: JobSiteGeo | null
  accessNotes?: string | null
  attendanceGraceMinutes?: number | null
  ownerRoleKey?: string | null
  dutyChain?: OpsPlaceDutyChain | null
  departmentKey?: string | null
  accessList?: string[] | null
  readAccess?: string[] | null
  accessRevision?: number
  accessJob?: OpsPlaceAccessJobProgress | null
  archived: boolean
  createdAt: unknown
  createdBy: string
  updatedAt: unknown
  updatedBy: string
}

/**
 * Computes effective place access list from an ordered chain of access lists
 * (ancestors root-first, ending with the place itself).
 *
 * Rule:
 * - If neither the place nor any ancestor has a list (all are null/undefined), returns null.
 * - Otherwise returns the intersection of every non-null accessList along path and the place itself.
 * - An empty list [] is an allowed restriction (nobody restricted sees it).
 */
export function computeEffectivePlaceAccess(
  chain: ReadonlyArray<ReadonlyArray<string> | string[] | null | undefined>,
): string[] | null {
  let hasAnyList = false
  let currentSet: Set<string> | null = null

  for (const list of chain) {
    if (list !== null && list !== undefined) {
      hasAnyList = true
      if (currentSet === null) {
        currentSet = new Set(list)
      } else {
        const nextSet = new Set<string>()
        for (const uid of list) {
          if (currentSet.has(uid)) {
            nextSet.add(uid)
          }
        }
        currentSet = nextSet
      }
    }
  }

  if (!hasAnyList || currentSet === null) {
    return null
  }
  return Array.from(currentSet)
}

/**
 * Validates that a child place's proposed access list does not name anyone
 * outside its parent's effective readAccess list (the "narrower" rule).
 */
export function validateChildPlaceAccessList(
  childList: ReadonlyArray<string> | string[] | null | undefined,
  parentEffectiveList: ReadonlyArray<string> | string[] | null | undefined,
): { valid: boolean; extraUids: string[] } {
  if (!childList || childList.length === 0) {
    return { valid: true, extraUids: [] }
  }
  if (!parentEffectiveList) {
    // Parent has no restriction, so child can set any list
    return { valid: true, extraUids: [] }
  }
  const allowed = new Set(parentEffectiveList)
  const extraUids = childList.filter((uid) => !allowed.has(uid))
  return {
    valid: extraUids.length === 0,
    extraUids,
  }
}

export interface OpsAccessJobCounts {
  scanned: number
  updated: number
}

export interface SetPlaceAccessInput {
  workspaceId: string
  placeId: string
  accessList: string[] | null
  opId?: string
}

export interface SetPlaceAccessResult {
  success: boolean
  placeId: string
  accessList: string[] | null
  readAccess: string[] | null
  accessRevision: number
  jobId: string
  replayed?: boolean
}

export interface OpsAccessJobCursor {
  phase: 'PLACES' | 'COPIES'
  collIndex: number
  placeIndex: number
  lastDocId: string | null
  stage?: 'PLACES' | 'COPIES'
  collectionIndex?: number
}

export interface OpsAccessJobDoc {
  id: string
  workspaceId: string
  rootPlaceId: string
  accessRevision: number
  state: OpsAccessJobState
  page?: number
  leaseOwner?: string | null
  leaseUntil?: number | null
  cursor?: OpsAccessJobCursor | null
  counts?: Record<string, OpsAccessJobCounts>
  subtreePlaceIds?: string[]
  attempts?: number
  lastError?: string | null
  startedAt?: unknown
  finishedAt?: unknown
  error?: string
  createdAt: unknown
  updatedAt?: unknown
}

export interface OpsPlaceTokenDoc {
  placeId: string
  workspaceId: string
  createdAt: unknown
  revokedAt: unknown | null
}

export interface ResolvedPlaceToken {
  placeId: string
  name: string
  level: OpsPlaceLevel
  path: string[]
  workspaceId: string
  sector: OpsSector
}

export interface CreatePlaceInput {
  workspaceId: string
  sector: OpsSector
  parentId?: string | null
  level: OpsPlaceLevel
  name: string
  code: string
  preset?: OpsPreset | null
  siteKind?: ClinicsSiteKind | null
  zoneKind?: ShopsZoneKind | null
  blockKind?: StaysBlockKind | null
  areaKind?: StaysAreaKind | null
  roomTypes?: StaysRoomType[] | null
  roomTypeId?: string | null
  minibarCheckOnCheckout?: boolean | null
  guestRequestsEnabled?: boolean | null
  customerId?: string | null
  address?: JobSiteAddress | null
  geo?: JobSiteGeo | null
  accessNotes?: string | null
  ownerRoleKey?: string | null
  hubPlaceId?: string | null
}

export interface RenamePlaceInput {
  workspaceId: string
  placeId: string
  name?: string
  code?: string
  preset?: OpsPreset | null
  siteKind?: ClinicsSiteKind | null
  zoneKind?: ShopsZoneKind | null
  blockKind?: StaysBlockKind | null
  areaKind?: StaysAreaKind | null
  attendanceGraceMinutes?: number | null
  hubPlaceId?: string | null
  opId?: string
}

export interface RenamePlaceResult {
  success: boolean
  placeId: string
  name: string
  code: string
  zoneKind?: ShopsZoneKind | null
  blockKind?: StaysBlockKind | null
  areaKind?: StaysAreaKind | null
  attendanceGraceMinutes?: number | null
  hubPlaceId?: string | null
  replayed?: boolean
}

export interface SetPlacePresetInput {
  workspaceId: string
  placeId: string
  preset: OpsPreset | null
  opId?: string
}

export interface SetPlacePresetResult {
  success: boolean
  placeId: string
  preset: OpsPreset | null
  replayed?: boolean
}

export interface SetPropertyRoomTypesInput {
  workspaceId: string
  propertyId: string
  roomTypes: StaysRoomType[]
  minibarCheckOnCheckout?: boolean | null
  opId: string
}

export interface SetPropertyRoomTypesResult {
  success: boolean
  propertyId: string
  roomTypes: StaysRoomType[]
  minibarCheckOnCheckout?: boolean | null
  replayed: boolean
}

export interface SetPropertyMinibarCheckInput {
  workspaceId: string
  propertyId: string
  minibarCheckOnCheckout: boolean
  opId: string
}

export interface SetPropertyMinibarCheckResult {
  success: boolean
  propertyId: string
  minibarCheckOnCheckout: boolean
  replayed: boolean
}

export interface SetRoomTypeInput {
  workspaceId: string
  placeIds: string[]
  roomTypeId: string
  opId: string
}

export interface SetRoomTypeResult {
  success: boolean
  count: number
  roomTypeId: string
  replayed: boolean
}

export interface MovePlaceInput {
  workspaceId: string
  placeId: string
  newParentId: string | null
}

export interface ArchivePlaceInput {
  workspaceId: string
  placeId: string
  archived?: boolean
}

export interface BulkPlaceInputItem {
  level: OpsPlaceLevel
  name: string
  code?: string
  preset?: OpsPreset | null
  siteKind?: ClinicsSiteKind | null
  zoneKind?: ShopsZoneKind | null
  blockKind?: StaysBlockKind | null
  areaKind?: StaysAreaKind | null
  roomTypes?: StaysRoomType[] | null
  roomTypeId?: string | null
  minibarCheckOnCheckout?: boolean | null
  guestRequestsEnabled?: boolean | null
  customerId?: string | null
  address?: JobSiteAddress | null
  geo?: JobSiteGeo | null
  accessNotes?: string | null
  ownerRoleKey?: string | null
  dutyChain?: OpsPlaceDutyChain | null
  departmentKey?: string | null
  children?: BulkPlaceInputItem[]
}

export interface SetPlaceDutyChainInput {
  workspaceId: string
  placeId: string
  dutyChain?: {
    inChargeRoleKey?: string | null
    backupRoleKey?: string | null
    onCallRoleKey?: string | null
  } | null
  departmentKey?: string | null
  ownerRoleKey?: string | null
  opId?: string
}

export interface SetPlaceDutyChainResult {
  success: boolean
  placeId: string
  dutyChain: OpsPlaceDutyChain | null
  departmentKey: string | null
  ownerRoleKey: string | null
}

export interface BulkCreatePlacesInput {
  workspaceId: string
  sector: OpsSector
  outline?: string
  items?: BulkPlaceInputItem[]
  parentId?: string | null
  preset?: OpsPreset | null
  opId: string
}

export interface BulkCreatePlacesResult {
  placeIds: string[]
  count: number
  opId: string
  replayed: boolean
}

export interface CreatePlaceTokenInput {
  workspaceId: string
  placeId: string
}

export interface CreatePlaceTokenResult {
  token: string
  placeId: string
}

export interface RevokePlaceTokenInput {
  workspaceId: string
  placeId: string
  token?: string
}

export interface ResolvePlaceTokenInput {
  token: string
}

export interface ParsedOutlineItem {
  depth: number
  level: OpsPlaceLevel
  name: string
  code: string
  parentIndex: number | null
  preset?: OpsPreset | null
  siteKind?: ClinicsSiteKind | null
  zoneKind?: ShopsZoneKind | null
  blockKind?: StaysBlockKind | null
  areaKind?: StaysAreaKind | null
  roomTypes?: StaysRoomType[] | null
  roomTypeId?: string | null
  minibarCheckOnCheckout?: boolean | null
  guestRequestsEnabled?: boolean | null
  customerId?: string | null
  address?: JobSiteAddress | null
  geo?: JobSiteGeo | null
  accessNotes?: string | null
  ownerRoleKey?: string | null
  dutyChain?: OpsPlaceDutyChain | null
  departmentKey?: string | null
}

export const MAX_PLACES_PER_BULK_CALL = 2000

/**
 * Parses an indented text outline into validated hierarchy nodes.
 * Validates level order per sector and indentation jumps.
 * Throws an Error with descriptive message if validation fails.
 */
export function parsePlacesOutline(
  outlineText: string,
  sector: OpsSector,
  parentLevel?: OpsPlaceLevel | null,
): ParsedOutlineItem[] {
  if (typeof outlineText !== 'string' || !outlineText.trim()) {
    throw new Error('Outline contains no place entries.')
  }

  const rawLines = outlineText.split(/\r?\n/)
  const lines: { lineNum: number; depth: number; text: string }[] = []

  for (let idx = 0; idx < rawLines.length; idx++) {
    const raw = rawLines[idx]
    if (!raw.trim()) continue

    // Count indentation: tabs or 2 spaces = 1 depth unit
    const leadingWhitespaceMatch = raw.match(/^[ \t]*/)
    const leading = leadingWhitespaceMatch ? leadingWhitespaceMatch[0] : ''
    let spacesCount = 0
    for (const char of leading) {
      if (char === '\t') spacesCount += 2
      else spacesCount += 1
    }
    const depth = Math.floor(spacesCount / 2)
    lines.push({ lineNum: idx + 1, depth, text: raw.trim() })
  }

  if (lines.length === 0) {
    throw new Error('Outline contains no place entries.')
  }

  if (lines.length > MAX_PLACES_PER_BULK_CALL) {
    throw new Error(`Places count (${lines.length}) exceeds maximum of 2,000 per call.`)
  }

  const items: ParsedOutlineItem[] = []
  // Stack of item indices by depth: stack[depth] = itemIndex
  const stack: number[] = []

  const allowedLevelsSet = new Set<string>(
    OPS_SECTORS[sector]?.levels ?? [],
  )

  for (let i = 0; i < lines.length; i++) {
    const { lineNum, depth, text } = lines[i]

    if (depth > 0 && depth > (stack.length ? lines[stack[stack.length - 1]].depth + 1 : 0)) {
      throw new Error(`Line ${lineNum}: Indentation jump not allowed (depth ${depth}).`)
    }

    // Determine parent index
    while (stack.length > depth) {
      stack.pop()
    }

    const parentIndex = stack.length > 0 ? stack[stack.length - 1] : null
    const effectiveParentLevel = parentIndex !== null ? items[parentIndex].level : (parentLevel ?? null)

    // Parse level, name, code from line text:
    // Formats:
    // 1) [LEVEL] Name (CODE)
    // 2) LEVEL: Name (CODE)
    // 3) Name (CODE)
    // 4) Name
    let level: OpsPlaceLevel | undefined
    let remaining = text

    const bracketLevelMatch = remaining.match(/^\[([A-Za-z0-9_]+)\]\s*(.*)$/)
    if (bracketLevelMatch) {
      const candidate = bracketLevelMatch[1].toUpperCase()
      if (allowedLevelsSet.has(candidate)) {
        level = candidate as OpsPlaceLevel
        remaining = bracketLevelMatch[2].trim()
      }
    } else {
      const colonLevelMatch = remaining.match(/^([A-Za-z0-9_]+):\s*(.*)$/)
      if (colonLevelMatch) {
        const candidate = colonLevelMatch[1].toUpperCase()
        if (allowedLevelsSet.has(candidate)) {
          level = candidate as OpsPlaceLevel
          remaining = colonLevelMatch[2].trim()
        }
      }
    }

    // Extract code in (CODE) or [CODE] at end of remaining
    let code: string | undefined
    const parenCodeMatch = remaining.match(/^(.*?)\s*[([]([A-Za-z0-9_.-]+)[)\]]\s*$/)
    if (parenCodeMatch) {
      remaining = parenCodeMatch[1].trim()
      code = parenCodeMatch[2].trim()
    }

    const name = remaining.trim()
    if (!name) {
      throw new Error(`Line ${lineNum}: Place name cannot be empty.`)
    }

    // If level not specified, infer from parent level
    if (!level) {
      if (!effectiveParentLevel) {
        level = ROOT_LEVEL[sector]
        if (!level) {
          throw new Error(`Line ${lineNum}: Sector "${sector}" does not support places.`)
        }
      } else {
        level = DEFAULT_CHILD_LEVEL[sector]?.[effectiveParentLevel]
        if (!level) {
          throw new Error(`Line ${lineNum}: Parent level "${effectiveParentLevel}" cannot have child places.`)
        }
      }
    }

    if (!allowedLevelsSet.has(level)) {
      throw new Error(`Line ${lineNum}: Level "${level}" is not valid for sector ${sector}.`)
    }

    // Auto-generate code if omitted
    if (!code) {
      code = `${level.slice(0, 3)}-${i + 1}`
    }

    // Validate level order transition
    if (!isValidChildLevel(sector, effectiveParentLevel, level)) {
      const parentLabel = effectiveParentLevel ?? 'root'
      throw new Error(
        `Line ${lineNum}: Invalid level order. Level "${level}" cannot be a child of "${parentLabel}" in sector ${sector}.`,
      )
    }

    items.push({
      depth,
      level,
      name,
      code,
      parentIndex,
    })

    stack[depth] = i
  }

  return items
}

/**
 * Flattens and validates a structured items tree for bulkCreatePlaces.
 * Enforces MAX_PLACES_PER_BULK_CALL, level order validation via isValidChildLevel,
 * and name/code cleaning. Preserves exact names without parsing parentheses as codes.
 */
export function flattenBulkPlaceItems(
  items: BulkPlaceInputItem[],
  sector: OpsSector,
  parentLevel?: OpsPlaceLevel | null,
): ParsedOutlineItem[] {
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error('Items list is empty.')
  }

  const result: ParsedOutlineItem[] = []
  const allowedLevelsSet = new Set<string>(OPS_SECTORS[sector]?.levels ?? [])

  function walk(
    nodes: BulkPlaceInputItem[],
    depth: number,
    parentIdx: number | null,
    currentParentLevel: OpsPlaceLevel | null,
  ) {
    for (const node of nodes) {
      if (result.length >= MAX_PLACES_PER_BULK_CALL) {
        throw new Error(`Places count exceeds maximum of ${MAX_PLACES_PER_BULK_CALL} per call.`)
      }

      const name = typeof node.name === 'string' ? node.name.trim() : ''
      if (!name) {
        throw new Error('Place name cannot be empty.')
      }
      if (name.length > 120) {
        throw new Error('Place name exceeds 120 characters.')
      }

      const level = node.level
      if (!level || !allowedLevelsSet.has(level)) {
        throw new Error(`Level "${level}" is not valid for sector ${sector}.`)
      }

      if (!isValidChildLevel(sector, currentParentLevel, level)) {
        const parentLabel = currentParentLevel ?? 'root'
        throw new Error(
          `Invalid level order. Level "${level}" cannot be a child of "${parentLabel}" in sector ${sector}.`,
        )
      }

      let code = typeof node.code === 'string' ? node.code.trim() : ''
      if (!code) {
        code = `${level.slice(0, 3)}-${result.length + 1}`
      }

      if (node.preset !== undefined && node.preset !== null) {
        if (!sectorAllowsPresetOnLevel(sector, level)) {
          throw new Error(`Preset is only allowed on a place level that accepts presets in sector ${sector}.`)
        }
        const def = OPS_SECTORS[sector]
        if (!def?.presets || !(def.presets as readonly string[]).includes(node.preset)) {
          throw new Error(`Invalid preset "${node.preset}" for sector ${sector}.`)
        }
      }

      if (node.siteKind !== undefined && node.siteKind !== null) {
        if (!isClinicsSector(sector) || level !== 'SITE') {
          throw new Error('Site kind is only allowed on SITE places in the clinics sector.')
        }
        if (!(CLINICS_SITE_KINDS as readonly string[]).includes(node.siteKind)) {
          throw new Error(`Invalid site kind "${node.siteKind}".`)
        }
      }

      if (node.zoneKind !== undefined && node.zoneKind !== null) {
        if (sector !== 'shops' || level !== 'ZONE') {
          throw new Error('Zone kind is only allowed on ZONE places in the shops sector.')
        }
        if (!(SHOPS_ZONE_KINDS as readonly string[]).includes(node.zoneKind)) {
          throw new Error(`Invalid zone kind "${node.zoneKind}".`)
        }
      }

      if (node.blockKind !== undefined && node.blockKind !== null) {
        if (sector !== 'stays' || level !== 'BLOCK') {
          throw new Error('Block kind is only allowed on BLOCK places in the stays sector.')
        }
        if (!(STAYS_BLOCK_KINDS as readonly string[]).includes(node.blockKind)) {
          throw new Error(`Invalid block kind "${node.blockKind}".`)
        }
      }

      if (node.areaKind !== undefined && node.areaKind !== null) {
        if (sector !== 'stays' || level !== 'AREA') {
          throw new Error('Area kind is only allowed on AREA places in the stays sector.')
        }
        if (!(STAYS_AREA_KINDS as readonly string[]).includes(node.areaKind)) {
          throw new Error(`Invalid area kind "${node.areaKind}".`)
        }
      }

      if (node.roomTypes !== undefined && node.roomTypes !== null) {
        if (sector !== 'stays' || level !== 'PROPERTY') {
          throw new Error('roomTypes is only allowed on PROPERTY places in the stays sector.')
        }
      }

      if (node.minibarCheckOnCheckout !== undefined && node.minibarCheckOnCheckout !== null) {
        if (sector !== 'stays' || level !== 'PROPERTY') {
          throw new Error('minibarCheckOnCheckout is only allowed on PROPERTY places in the stays sector.')
        }
      }

      if (node.guestRequestsEnabled !== undefined && node.guestRequestsEnabled !== null) {
        if (sector !== 'stays' || level !== 'PROPERTY') {
          throw new Error('guestRequestsEnabled is only allowed on PROPERTY places in the stays sector.')
        }
      }

      if (node.roomTypeId !== undefined && node.roomTypeId !== null) {
        if (sector !== 'stays' || level !== 'ROOM') {
          throw new Error('roomTypeId is only allowed on ROOM places in the stays sector.')
        }
      }

      if (sector === 'stays' && level === 'BED') {
        let currIdx = parentIdx
        let ancestorPropertyPreset: string | null = null
        while (currIdx !== null) {
          const anc = result[currIdx]
          if (anc.level === 'PROPERTY') {
            ancestorPropertyPreset = anc.preset ?? null
            break
          }
          currIdx = anc.parentIndex
        }
        if (ancestorPropertyPreset !== null && ancestorPropertyPreset !== 'HOSTEL') {
          throw new Error('BED places are only allowed under a property with HOSTEL preset.')
        }
      }

      if (sector === 'jobs') {
        if (level === 'SITE') {
          if (!node.customerId) {
            throw new Error('customerId is required on Jobs SITE.')
          }
          if (!node.address || !node.address.line1 || !node.address.city) {
            throw new Error('address (line1 and city) is required on Jobs SITE.')
          }
        } else if (level === 'SERVICE_AREA') {
          if (node.customerId || node.address || node.geo || node.accessNotes) {
            throw new Error('Jobs site fields are not allowed on Jobs SERVICE_AREA.')
          }
        }
      } else {
        if (node.customerId || node.address || node.geo || node.accessNotes) {
          throw new Error('Jobs fields are only allowed in the jobs sector.')
        }
      }

      const itemIdx = result.length
      result.push({
        depth,
        level,
        name,
        code,
        parentIndex: parentIdx,
        preset: node.preset ?? null,
        zoneKind: node.zoneKind ?? null,
        ...(node.ownerRoleKey !== undefined ? { ownerRoleKey: node.ownerRoleKey } : {}),
        ...(node.dutyChain !== undefined ? { dutyChain: node.dutyChain } : {}),
        ...(node.departmentKey !== undefined ? { departmentKey: node.departmentKey } : {}),
        ...(sector === 'stays'
          ? {
              blockKind: node.blockKind ?? null,
              areaKind: node.areaKind ?? null,
              roomTypes: node.roomTypes ?? null,
              roomTypeId: node.roomTypeId ?? null,
              minibarCheckOnCheckout: node.minibarCheckOnCheckout ?? null,
              guestRequestsEnabled: node.guestRequestsEnabled ?? null,
            }
          : {}),
        ...(sector === 'jobs' && level === 'SITE'
          ? {
              customerId: node.customerId ?? null,
              address: node.address ?? null,
              geo: node.geo ?? null,
              accessNotes: node.accessNotes ?? null,
            }
          : {}),
        ...(isClinicsSector(sector) && level === 'SITE'
          ? {
              siteKind: node.siteKind ?? null,
            }
          : {}),
      })

      if (node.children && Array.isArray(node.children) && node.children.length > 0) {
        walk(node.children, depth + 1, itemIdx, level)
      }
    }
  }

  walk(items, 0, null, parentLevel ?? null)
  return result
}

export const DUE_CHECK_UNIT_FILTER_LEVELS = [
  'DEPARTMENT',
  'UNIT',
  'WARD',
  'SITE',
  'BUILDING',
] as const

export type DueCheckUnitFilterLevel = (typeof DUE_CHECK_UNIT_FILTER_LEVELS)[number]

